import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { useSEO, generateMedicalWebPageSchema } from '../../utils/seo';

const PreventionPage = () => {
  // SEO Configuration
  useSEO({
    title: 'Cervical Cancer Prevention & Screening Guide | HPV Vaccine & Pap Test',
    description: 'Complete guide to cervical cancer prevention and screening. Learn about HPV vaccination, Pap test schedules, screening timelines by age, and lifestyle prevention tips. Cervical cancer is 90% preventable with proper screening.',
    keywords: 'cervical cancer prevention, HPV vaccine, Pap test, cervical screening, cancer prevention tips, HPV vaccination India',
    canonical: '/prevention',
    schema: generateMedicalWebPageSchema(
      'Cervical Cancer Prevention & Screening Guide',
      'Comprehensive information about cervical cancer prevention, HPV vaccination, and screening guidelines',
      '/prevention',
      ['Patient', 'General Public']
    ),
  });

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <section className="relative h-96 flex items-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-accent to-purple-700"></div>
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/e8366158b032c805754e5aef2df1cdc4.jpg"
            alt="Prevention"
            className="w-full h-full object-cover object-top opacity-20"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-white">
          <h1 className="text-5xl md:text-6xl font-bold font-display mb-4">
            Prevention & Screening Guide
          </h1>
          <p className="text-xl text-white/90 max-w-2xl">
            Cervical cancer is one of the most preventable cancers. Learn how to protect yourself.
          </p>
        </div>
      </section>

      <section className="py-24 bg-gradient-to-b from-white to-purple-50/30">
        <div className="max-w-7xl mx-auto px-6">
          <div className="bg-gradient-to-br from-primary/10 to-accent/10 rounded-3xl p-12 mb-24">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold font-display text-gray-800 mb-4">
                The Power of <span className="text-primary">Prevention</span>
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                With regular screening and preventive measures, cervical cancer can be detected early or prevented entirely. Here's what you need to know.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  icon: 'ri-shield-check-line',
                  title: 'HPV Vaccination',
                  description: 'Protects against high-risk HPV types that cause most cervical cancers',
                  stat: '90%',
                  statLabel: 'Protection Rate',
                },
                {
                  icon: 'ri-microscope-line',
                  title: 'Regular Screening',
                  description: 'Detects precancerous changes before they become cancer',
                  stat: '93%',
                  statLabel: 'Early Detection',
                },
                {
                  icon: 'ri-heart-pulse-line',
                  title: 'Healthy Lifestyle',
                  description: 'Reduces risk factors and supports overall cervical health',
                  stat: '50%',
                  statLabel: 'Risk Reduction',
                },
              ].map((item, index) => (
                <div key={index} className="bg-white rounded-2xl p-8 shadow-lg text-center">
                  <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-primary to-accent rounded-full mx-auto mb-4">
                    <i className={`${item.icon} text-3xl text-white`}></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">{item.title}</h3>
                  <p className="text-gray-600 mb-4 text-sm">{item.description}</p>
                  <div className="text-3xl font-bold text-primary mb-1">{item.stat}</div>
                  <div className="text-xs text-gray-500 uppercase tracking-wide">{item.statLabel}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="mb-24">
            <h2 className="text-3xl font-bold font-display text-gray-800 mb-12 text-center">
              Screening <span className="text-primary">Timeline</span>
            </h2>
            <div className="relative">
              <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-gradient-to-b from-primary via-accent to-secondary hidden md:block"></div>
              <div className="space-y-12">
                {[
                  {
                    age: 'Ages 21-29',
                    recommendation: 'Pap Test Every 3 Years',
                    details: 'Begin cervical cancer screening at age 21. Pap test alone is recommended every 3 years. HPV testing is not recommended for this age group.',
                    icon: 'ri-calendar-check-line',
                  },
                  {
                    age: 'Ages 30-65',
                    recommendation: 'Pap + HPV Test Every 5 Years',
                    details: 'Preferred: Pap test combined with HPV test every 5 years. Alternative: Pap test alone every 3 years. This is the most important screening period.',
                    icon: 'ri-shield-check-line',
                  },
                  {
                    age: 'Ages 65+',
                    recommendation: 'May Stop if Previous Tests Normal',
                    details: 'Women over 65 with adequate prior screening and no history of precancer can stop screening. Discuss with your healthcare provider.',
                    icon: 'ri-user-heart-line',
                  },
                ].map((item, index) => (
                  <div key={index} className="relative">
                    <div className={`flex flex-col md:flex-row gap-8 items-center ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                      <div className="flex-1 bg-white rounded-2xl p-8 shadow-lg">
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 flex items-center justify-center bg-primary/10 rounded-lg flex-shrink-0">
                            <i className={`${item.icon} text-2xl text-primary`}></i>
                          </div>
                          <div className="flex-1">
                            <div className="text-sm font-semibold text-primary mb-1">{item.age}</div>
                            <h3 className="text-xl font-bold text-gray-800 mb-2">{item.recommendation}</h3>
                            <p className="text-gray-600 leading-relaxed">{item.details}</p>
                          </div>
                        </div>
                      </div>
                      <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-primary to-accent rounded-full shadow-lg z-10 flex-shrink-0">
                        <span className="text-white font-bold text-xl">{index + 1}</span>
                      </div>
                      <div className="flex-1 hidden md:block"></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="bg-white rounded-3xl shadow-xl p-12 mb-24">
            <h2 className="text-3xl font-bold font-display text-gray-800 mb-8 text-center">
              HPV <span className="text-accent">Vaccination</span>
            </h2>
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-6">
                <div className="bg-accent/10 rounded-xl p-6">
                  <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
                    <i className="ri-shield-check-line text-accent text-xl"></i>
                    What is HPV Vaccine?
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    The HPV vaccine protects against human papillomavirus, which causes most cervical cancers. It's one of the most effective cancer prevention tools available.
                  </p>
                </div>
                <div className="bg-primary/10 rounded-xl p-6">
                  <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
                    <i className="ri-user-line text-primary text-xl"></i>
                    Who Should Get Vaccinated?
                  </h3>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-primary mt-0.5 flex-shrink-0"></i>
                      <span><strong>Ages 11-12:</strong> Routine vaccination recommended</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-primary mt-0.5 flex-shrink-0"></i>
                      <span><strong>Ages 13-26:</strong> Catch-up vaccination if not previously vaccinated</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-primary mt-0.5 flex-shrink-0"></i>
                      <span><strong>Ages 27-45:</strong> Discuss with healthcare provider</span>
                    </li>
                  </ul>
                </div>
                <div className="bg-green-50 rounded-xl p-6">
                  <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
                    <i className="ri-heart-pulse-line text-green-600 text-xl"></i>
                    Benefits
                  </h3>
                  <ul className="space-y-2 text-gray-700 text-sm">
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-green-600 mt-0.5 flex-shrink-0"></i>
                      <span>Prevents up to 90% of HPV-related cancers</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-green-600 mt-0.5 flex-shrink-0"></i>
                      <span>Safe and effective with minimal side effects</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-green-600 mt-0.5 flex-shrink-0"></i>
                      <span>Long-lasting protection</span>
                    </li>
                  </ul>
                </div>
              </div>
              <div>
                <img
                  src="https://static.readdy.ai/image/6006b18c50b98974f62513d00886a423/3404469ffddfa31b5eb308491bc5c8f3.jpeg"
                  alt="HPV Vaccination - Doctor with Patient"
                  className="w-full h-auto rounded-2xl shadow-xl"
                />
              </div>
            </div>
          </div>

          <div className="mb-24">
            <h2 className="text-3xl font-bold font-display text-gray-800 mb-12 text-center">
              Lifestyle <span className="text-primary">Prevention Tips</span>
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                {
                  icon: 'ri-forbid-2-line',
                  title: 'Don\'t Smoke',
                  description: 'Smoking doubles cervical cancer risk and reduces treatment effectiveness',
                  color: 'red',
                },
                {
                  icon: 'ri-shield-user-line',
                  title: 'Practice Safe Sex',
                  description: 'Use protection to reduce HPV transmission risk',
                  color: 'blue',
                },
                {
                  icon: 'ri-apple-line',
                  title: 'Healthy Diet',
                  description: 'Eat plenty of fruits and vegetables to support immune system',
                  color: 'green',
                },
                {
                  icon: 'ri-heart-pulse-line',
                  title: 'Regular Exercise',
                  description: 'Maintain healthy weight and boost overall health',
                  color: 'orange',
                },
                {
                  icon: 'ri-mental-health-line',
                  title: 'Manage Stress',
                  description: 'Chronic stress can weaken immune system',
                  color: 'purple',
                },
                {
                  icon: 'ri-calendar-check-line',
                  title: 'Regular Checkups',
                  description: 'Don\'t skip annual health examinations',
                  color: 'teal',
                },
                {
                  icon: 'ri-medicine-bottle-line',
                  title: 'Limit Contraceptive Use',
                  description: 'Discuss long-term oral contraceptive use with doctor',
                  color: 'pink',
                },
                {
                  icon: 'ri-information-line',
                  title: 'Stay Informed',
                  description: 'Keep up with latest cervical health information',
                  color: 'indigo',
                },
              ].map((tip, index) => (
                <div
                  key={index}
                  className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 group"
                >
                  <div className={`w-14 h-14 flex items-center justify-center bg-${tip.color}-100 rounded-xl mb-4 group-hover:scale-110 transition-transform duration-300`}>
                    <i className={`${tip.icon} text-2xl text-${tip.color}-600`}></i>
                  </div>
                  <h3 className="font-bold text-gray-800 mb-2">{tip.title}</h3>
                  <p className="text-sm text-gray-600">{tip.description}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-3xl p-12">
            <h2 className="text-3xl font-bold font-display text-gray-800 mb-8 text-center">
              Myth vs <span className="text-secondary">Fact</span>
            </h2>
            <div className="space-y-6">
              {[
                {
                  myth: 'Only older women get cervical cancer',
                  fact: 'Cervical cancer can affect women of all ages, though it\'s most common in women over 30. Screening should begin at age 21.',
                },
                {
                  myth: 'If I have HPV, I will definitely get cervical cancer',
                  fact: 'Most HPV infections clear on their own. Only persistent high-risk HPV infections can lead to cervical cancer over many years.',
                },
                {
                  myth: 'Pap tests are painful and uncomfortable',
                  fact: 'Pap tests are quick (5 minutes) and cause minimal discomfort. The benefits far outweigh any brief discomfort.',
                },
                {
                  myth: 'I don\'t need screening if I\'m not sexually active',
                  fact: 'While HPV is sexually transmitted, screening is still important as HPV can remain dormant for years.',
                },
                {
                  myth: 'HPV vaccine is only for young girls',
                  fact: 'HPV vaccine is recommended for both boys and girls, and catch-up vaccination is available for adults up to age 26.',
                },
              ].map((item, index) => (
                <div key={index} className="grid md:grid-cols-2 gap-6">
                  <div className="bg-red-50 border-l-4 border-red-500 rounded-lg p-6">
                    <div className="flex items-start gap-3">
                      <i className="ri-close-circle-fill text-red-500 text-2xl flex-shrink-0"></i>
                      <div>
                        <div className="text-xs font-semibold text-red-600 uppercase mb-1">Myth</div>
                        <p className="text-gray-800 font-medium">{item.myth}</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-green-50 border-l-4 border-green-500 rounded-lg p-6">
                    <div className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-fill text-green-500 text-2xl flex-shrink-0"></i>
                      <div>
                        <div className="text-xs font-semibold text-green-600 uppercase mb-1">Fact</div>
                        <p className="text-gray-800">{item.fact}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold font-display text-gray-800 mb-6">
            Ready to Take Control of Your Health?
          </h2>
          <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
            Start your cervical health assessment or find healthcare facilities near you
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/consent"
              className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-primary to-teal-500 text-white rounded-full text-lg font-semibold hover:shadow-2xl hover:scale-105 transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              Start Health Check
              <i className="ri-arrow-right-line text-xl"></i>
            </Link>
            <Link
              to="/hospital-directory"
              className="inline-flex items-center gap-3 px-8 py-4 border-2 border-primary text-primary rounded-full text-lg font-semibold hover:bg-primary hover:text-white transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              Find Hospitals
              <i className="ri-hospital-line text-xl"></i>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PreventionPage;
